// Open where you left off (Features 4, onboarding part 6): the board, and the page on a doc or plan board, the account
// was on last, on any of its devices (shared/onboarding.ts last). A first load opens it unless a link says where to go
// (a notification's ?notice=, a shared board's ?shared=, a board's own ?board=), or this browser moved to another board since. A place this
// same browser kept is the one it remembers itself (cb:board), so a reload stays where it was. Pure, so the store can
// call it as a snapshot loads.

import { cleanOnboarding } from "../../shared/onboarding";
import type { Board, ID } from "../../shared/types";
import { readStored, writeStored } from "../lib/stored";
import { randomString } from "../../shared/ids";

/** When this browser last opened a board (state/actions.ts switchBoard keeps it beside cb:board). */
export const HERE_AT = "cb:board:at";
const DEV = "cb:dev";

/** This browser, as the account's last place names it: a few random letters, kept here. */
export function deviceId(): string {
  let d = readStored(DEV);
  if (!d || !/^[a-z0-9]{6,16}$/.test(d)) {
    d = randomString(10);
    if (!writeStored(DEV, d)) return "nostorage";
  }
  return d;
}

/**
 * The board a page's own address names (/?board=<id>, src/state/navigate.ts): a link, a bookmark, a new tab, Back or
 * Forward, and a reload, whose address is the board this tab was on. Another tab of the same browser may have opened
 * another board since, and a reload of a board just made opened that one instead (David, 1 Nov).
 */
export function linkedBoard(): string | null {
  try {
    return new URLSearchParams(location.search).get("board");
  } catch {
    return null;
  }
}

/** Whether this page was reloaded, rather than opened at its address. */
function reloaded(): boolean {
  try {
    return (performance.getEntriesByType?.("navigation")?.[0] as PerformanceNavigationTiming | undefined)?.type === "reload";
  } catch {
    return false;
  }
}

/** Whether the page's address says where to go. */
function linked(): boolean {
  if (typeof location === "undefined") return false;
  const q = new URLSearchParams(location.search);
  return q.has("notice") || q.has("shared") || (q.has("board") && linkedBoard() !== null);
}

/**
 * The account's last place, when a first load should open it: a board in use, no link says otherwise, and this browser
 * remembers no board, or another device was there after this browser last opened one. Null to keep what this browser
 * remembers.
 */
export function leftOff(raw: unknown, boards: Board[], here: ID | null): { board: ID; page: ID | null } | null {
  const last = cleanOnboarding(raw).last;
  if (!last || linked() || !boards.some((b) => b.id === last.board)) return null;
  // this browser's own last place: what it remembers itself is as new, its open page too
  if (last.dev === deviceId()) return null;
  // a reload stays on the board this browser had open, wherever another device went since (David, 1 Nov: a reload of a
  // board just made opened another)
  if (here && reloaded()) return null;
  if (!here || here === last.board) return { board: last.board, page: last.page ?? null };
  // a board this browser was told to open without opening one since (from before this build, or a page's own setup)
  // stands: only a browser that has opened a board since, earlier than another device's last place, moves on
  const hereAt = Number(readStored(HERE_AT)) || Infinity; // no storage: what it remembers stands
  return hereAt >= last.at ? null : { board: last.board, page: last.page ?? null };
}
